import { useState } from 'react'

import { stringifyGraph } from '@/clients/http'

import { PosReportModel, type CashInOut } from '@/http/models/PosReport'
import { useBusiness } from '@/contexts/BusinessContext'

import { Badge } from '@/components/ui/badge'

import { PageHeader } from '@/components/atoms'
import { FilterSelect } from '@/components/features/pos/FilterSelect'
import { formatDateTime } from '@/components/features/pos/format'
import { PosListCard } from '@/components/features/pos/PosListCard'
import { usePagedReport } from '@/components/features/pos/usePagedReport'
import { usePosLookups } from '@/components/features/pos/usePosLookups'

const GRAPH = stringifyGraph({ '*': true, staff: { name: true } })

/** Cash put in or taken out of the tills, every branch (checklist C23). Recorded on the till and synced. */
const CashHistory = () => {
  const { formatCurrency } = useBusiness()
  const lookups = usePosLookups()
  const [type, setType] = useState('')
  const [terminal, setTerminal] = useState('')
  const list = usePagedReport<CashInOut>(PosReportModel.getCashInOuts, {
    type: type || undefined,
    terminal_id: terminal || undefined,
    graph_enc: GRAPH,
  })

  return (
    <div className="space-y-4">
      <PageHeader title="Cash in and out" description="Cash added to or removed from a terminal during a shift, across all branches." />
      <PosListCard<CashInOut>
        {...list}
        onPageChange={list.setPage}
        empty="No cash moves yet."
        filters={
          <>
            <FilterSelect label="Type" value={type} onChange={setType} allLabel="Cash in and out" options={[{ label: 'Cash in', value: 'cash_in' }, { label: 'Cash out', value: 'cash_out' }]} />
            <FilterSelect label="Terminal" value={terminal} onChange={setTerminal} allLabel="All terminals" options={lookups.terminalOptions} />
          </>
        }
        columns={[
          { label: 'Date', render: (r) => formatDateTime(r.created_at) },
          { label: 'Branch', render: (r) => lookups.branchOfTerminal(r.terminal_id), className: 'hidden md:table-cell' },
          { label: 'Terminal', render: (r) => lookups.terminalName(r.terminal_id) },
          { label: 'Staff', render: (r) => r.staff?.name ?? '—', className: 'hidden md:table-cell' },
          { label: 'Type', render: (r) => <Badge tone={r.type === 'cash_in' ? 'success' : 'warning'}>{r.type === 'cash_in' ? 'Cash in' : 'Cash out'}</Badge> },
          { label: 'Amount', render: (r) => <span className="tabular-nums">{formatCurrency(r.amount)}</span>, className: 'text-right' },
          { label: 'Note', render: (r) => r.remark || '—', className: 'hidden lg:table-cell' },
        ]}
      />
    </div>
  )
}

export default CashHistory
